import { useEffect, useState, useRef } from 'react';
import { logger } from './utils/logger';
import { unlockAudioPlayback } from './utils/audioHelpers';
import { LayoutManager } from './components/UI/LayoutManager';
import { LottieAvatar } from './components/Avatar/LottieAvatar';
import { GaliaScene } from './components/Scene/GaliaScene';
import { AchievementToast } from './components/UI/AchievementToast';
import { TrophyChestButton, TrophyChestPanel } from './components/UI/TrophyChest';
import achievementService, { ACHIEVEMENT_UNLOCKED_EVENT } from './services/achievementService';
import { detectLocalRecognitions, refineRecognitions, TROPHIES_BY_ID } from './config/trophies';
import { ActionAchievement, detectActions } from './config/actionAchievements';
import { MicrophoneButton } from './components/UI/MicrophoneButton';
import { MicHelpButton, MicHelpPanel } from './components/UI/MicHelp';
import { InfoButton } from './components/UI/InfoButton';
import { SettingsButton } from './components/UI/SettingsButton';
import { ConfigChip } from './components/UI/ConfigChip';
import { HomeButton } from './components/UI/HomeButton';
import { InitialScreen } from './components/UI/InitialScreen';
import { Chat } from './components/Chat/Chat';
import { useWebSocket } from './hooks/useWebSocket';
import { useAvatarAnimation } from './hooks/useAvatarAnimation';
import { useScreenOrientation } from './hooks/useScreenOrientation';
import { useVersionCheck } from './hooks/useVersionCheck';
import { loadPolyfills } from './utils/polyfills';
import { isEmbedded, openStandalone } from './utils/embed';
import { isDirectAccessMode, isGamificationUIEnabled, isKioskMode, kioskVisitEmail, isWorkspaceLayoutEnabled } from './utils/featureFlags';
import DirectLogin from './components/UI/DirectLogin';
import SessionExpired from './components/UI/SessionExpired';
import sessionService, { SessionIdentity } from './services/sessionService';

/** Email efectivo para la capa de TROFEOS: el del estudiante, o la identidad
 *  efímera del kiosco (solo trofeos — el guardado de conversaciones y las
 *  métricas siguen gateados por el email real y quedan apagados en kiosco). */
function trophyEmail(): string {
  return configService.getConfig().email || (isKioskMode() ? kioskVisitEmail() : '');
}

import configService from './services/configService';
import authService from './services/authService';
import webSocketService from './services/webSocketService';
import { extractTextFromFile } from './utils/extractDocumentText';
import interactionTimeService from './services/interactionTimeService';
import conversationSaverService from './services/conversationSaverService';
import { tryResumeConversation, markConversationReset } from './services/conversationResumeService';
import { getRecentConversations, formatTabLabel, type RecentConversation } from './services/recentConversationsService';
import type { ChatTab } from './components/Chat/Chat';
import { NewConversationButton } from './components/UI/NewConversationButton';
import { WelcomeGuide } from './components/UI/WelcomeGuide';
import { WorkspaceLayout } from './components/Layout/WorkspaceLayout';
import { getPromptReference } from './components/UI/InitialScreen';
import browserCloseHandler from './services/browserCloseHandler';
import supabaseService from './services/supabaseService';
import academicService from './services/academicService';
import AcademicMode from './components/Academic/AcademicMode';
import { UserConfiguration, PracticeMode } from './types';
import { enableTestAcademicMode } from './utils/testAcademicMode';
import { FeedbackBanner } from './components/UI/FeedbackBanner';
import feedbackService from './services/feedbackService';
import { StreakAchievementModal } from './components/UI/StreakAchievementModal';
import gamificationService from './services/gamificationService';
import TeacherActivityPanel from './components/Teacher/TeacherActivityPanel';
import TeacherActivityForm from './components/Teacher/TeacherActivityForm';
import TeacherActivityPreview from './components/Teacher/TeacherActivityPreview';
import StudentActivityList from './components/Student/StudentActivityList';
import StudentActivityView from './components/Student/StudentActivityView';
import { ActivityEmailForm } from './components/Student/ActivityEmailForm';
import { teacherActivityService } from './services/teacherActivityService';
import { TeacherActivity } from './types';
import { ObjectivesCompactTracker } from './components/UI/ObjectivesCompactTracker';
import { ObjectivesDetailPanel } from './components/UI/ObjectivesDetailPanel';
import { ContentsButton } from './components/UI/ContentsButton';
import { ContentViewer } from './components/UI/ContentViewer';
import { AudioToggleButton } from './components/UI/AudioToggleButton';
import './App.css';

// Función helper para determinar promptReference considerando el role del usuario
const getPromptReferenceWithRole = (
  mode: PracticeMode, 
  role: string | undefined | null, 
  isTestingPhase: boolean
): string => {
  const suffix = isTestingPhase ? '-dev' : '-pro';
  
  // Si no hay role, usar lógica por defecto basada solo en el modo
  if (!role) {
    const baseMap: Record<PracticeMode, string> = {
      'conversacional': 'alianzafrancesa-galia-conversacional',
      'estudio': 'alianzafrancesa-galia-ruta-academica',
      'examen': 'alianzafrancesa-galia-examen',
      'actividades-docente': 'alianzafrancesa-galia-actividades-docente',
      'actividades-estudiante': 'alianzafrancesa-galia-actividades-estudiante',
    };
    return `${baseMap[mode]}${suffix}`;
  }
  
  // Mapeo base según el modo
  const baseMap: Record<PracticeMode, string> = {
    'conversacional': 'alianzafrancesa-galia-conversacional',
    'estudio': 'alianzafrancesa-galia-ruta-academica',
    'examen': 'alianzafrancesa-galia-examen',
    'actividades-docente': 'alianzafrancesa-galia-actividades-docente',
    'actividades-estudiante': 'alianzafrancesa-galia-actividades-estudiante',
  };
  
  // Ajustar según el role
  // "Undefined" se trata como estudiante para la interfaz
  const roleLower = role?.toLowerCase() || '';
  const isUndefined = role === 'Undefined' || role === 'undefined';
  const isTeacher = !isUndefined && (roleLower === 'teacher' || roleLower.includes('docente') || roleLower.includes('instructor') || roleLower.includes('profesor'));
  const isStudent = isUndefined || roleLower === 'student' || roleLower.includes('estudiante') || roleLower.includes('learner') || roleLower.includes('alumno');

  // Si el modo es "actividades", forzar el prompt según el role
  if (mode === 'actividades-docente' || mode === 'actividades-estudiante') {
    // Ya está correcto según el modo
    const result = `${baseMap[mode]}${suffix}`;
    return result;
  }
  
  // Para otros modos, ajustar según el role si es necesario
  // Si es teacher y el modo es estudio, podría usar un prompt específico para docentes
  // Por ahora, todos los modos (excepto actividades) usan el mismo prompt independientemente del role
  // Pero podemos agregar lógica específica aquí si es necesario
  
  const result = `${baseMap[mode]}${suffix}`;
  return result;
};

function App() {
  const [shouldConnect, setShouldConnect] = useState(false);
  const { messages, sendAudioMessage, sendTextMessage, isConnecting, isTranscribing, notRecognizedSignal } = useWebSocket(shouldConnect);
  const { setHablando, resetFirstInteraction } = useAvatarAnimation();

  // Rastrear cambios en shouldConnect para debug
  useEffect(() => {
    if (shouldConnect) {
    }
  }, [shouldConnect]);

  // Aviso transitorio cuando el STT no entendió el audio del usuario. Antes el
  // fallo era silencioso (el turno se descartaba sin avisar), lo que en una app
  // de idiomas es ambiguo: el alumno no sabía si Galia no escuchó o si su francés
  // estuvo mal. Mostramos un mensaje breve y lo auto-ocultamos a los 4s.
  const [showNotRecognized, setShowNotRecognized] = useState(false);
  useEffect(() => {
    if (notRecognizedSignal === 0) return; // valor inicial, no mostrar
    setShowNotRecognized(true);
    const t = setTimeout(() => setShowNotRecognized(false), 4000);
    return () => clearTimeout(t);
  }, [notRecognizedSignal]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialConfigComplete, setInitialConfigComplete] = useState(false);

  // Auto-reload silencioso cuando hay un deploy nuevo en Vercel (ver
  // src/hooks/useVersionCheck.ts). Se le pasa initialConfigComplete para que NO
  // recargue a mitad de conversación: si aparece una versión nueva mientras el
  // usuario habla con Galia, difiere la recarga hasta que vuelva al menú. Antes
  // recargaba en seco y se percibía como "Galia se cierra sola" (reporte
  // Santiago, 2026-07-01).
  useVersionCheck(initialConfigComplete);
  const [userConfiguration, setUserConfiguration] = useState<UserConfiguration | null>(null);
  const previousConfigurationRef = useRef<UserConfiguration | null>(null); // Guardar configuración anterior para comparar
  const [isChatVisible, setIsChatVisible] = useState(false);

  // ── Logros por acción (gamificación) ──
  // Detecta acciones repetidas del estudiante (saludar, agradecer, preguntar…)
  // sobre los mensajes que llegan EN VIVO y muestra un trofeo al desbloquear.
  // La parte gráfica (paisaje + trofeos) está en construcción: solo build dev
  // hasta que la AF entregue el arte final (acuerdo Santiago 2026-07-09).
  const gamificationUI = isGamificationUIEnabled();
  // Rediseño opción 1a «Espace de travail» (solo dev): ver WorkspaceLayout.tsx.
  const workspaceLayout = isWorkspaceLayoutEnabled();
  const [achievementQueue, setAchievementQueue] = useState<ActionAchievement[]>([]);
  // Cofre de trofeos: panel galería + badge del botón. El badge cuenta solo
  // los trofeos NUEVOS desde la última apertura del cofre (no el total).
  const [showTrophyChest, setShowTrophyChest] = useState(false);
  const [showMicHelp, setShowMicHelp] = useState(false);
  /**
   * Bienvenida de primera conexión (informe UX 11/09/2026, hallazgos 1/4/7;
   * pedida por Santiago el 15/09). Se muestra una sola vez por usuario: el
   * flag viaja con el progreso en Supabase, no solo en localStorage, para que
   * no reaparezca en la PWA instalada ni en otro perfil del navegador.
   * En el kiosco NO se muestra: el visitante pasa uno o dos minutos frente a
   * la pantalla y la sesión es anónima.
   */
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    if (!initialConfigComplete || isKioskMode()) return;
    let cancelled = false;
    achievementService.hasSeenWelcome(trophyEmail()).then((seen) => {
      if (!cancelled && !seen) setShowWelcome(true);
    });
    return () => { cancelled = true; };
  }, [initialConfigComplete]);
  const [trophyCount, setTrophyCount] = useState(0);
  useEffect(() => {
    if (!gamificationUI || !initialConfigComplete) return;
    const email = trophyEmail();
    let cancelled = false;
    const refresh = () =>
      achievementService.getUnseenCount(email).then((n) => {
        if (!cancelled) setTrophyCount(n);
      });
    refresh();
    window.addEventListener(ACHIEVEMENT_UNLOCKED_EVENT, refresh);
    return () => {
      cancelled = true;
      window.removeEventListener(ACHIEVEMENT_UNLOCKED_EVENT, refresh);
    };
  }, [gamificationUI, initialConfigComplete]);
  // Índice hasta el que ya procesamos `messages`. Null = aún no inicializado;
  // se ancla al final del historial al montar para NO recontar mensajes viejos.
  const achProcessedRef = useRef<number | null>(null);
  useEffect(() => {
    const email = trophyEmail();
    const isProfMode = userConfiguration?.mode === 'actividades-docente';
    if (!gamificationUI || !email || isProfMode) return; // solo estudiantes, solo build dev
    if (achProcessedRef.current === null) {
      achProcessedRef.current = messages.length; // anclar; ignorar historial previo
      return;
    }
    if (messages.length <= achProcessedRef.current) return;
    const fresh = messages.slice(achProcessedRef.current);
    achProcessedRef.current = messages.length;
    const userMsgs = fresh.filter((m) => m.role === 'user' && m.content);
    if (userMsgs.length === 0) return;
    (async () => {
      for (const m of userMsgs) {
        const unlocked = await achievementService.processMessage(email, m.content);
        // Trofeos `local` (formas exactas: être, saludos, números, preguntas…):
        // reconocimiento determinista por regex sobre el texto literal, sin
        // pasar por el LLM. Fix del feedback de Santiago 26/08: tras 40 min el
        // modelo dejaba être en 3/6 y preguntas en 7/8 pese a uso constante.
        if (!isKioskMode()) {
          const recs = detectLocalRecognitions(m.content, userConfiguration?.mcerLevel);
          if (recs.length > 0) {
            const extra = await achievementService.processRecognitions(email, recs);
            unlocked.push(...extra);
          }
        }
        // Kiosco: respaldo local de los 3 trofeos. El reconocimiento del LLM
        // falla un turno de vez en cuando (varianza aceptable con contadores
        // acumulativos), pero en recepción el visitante saluda/pregunta/se
        // despide UNA vez: si ese turno se pierde, no ve nada. La detección
        // por regex de actionAchievements cubre justo esos tres gestos, así
        // que se usa como red — processRecognitions ignora duplicados.
        if (isKioskMode()) {
          const KIOSK_FALLBACK: Record<string, string> = {
            greeting: 'kiosk_saluer',
            question: 'kiosk_question',
            farewell: 'kiosk_adieu',
          };
          const recs = detectActions(m.content)
            .map((a) => KIOSK_FALLBACK[a])
            .filter(Boolean)
            .map((id) => ({ id, slot: 'fois' }));
          if (recs.length > 0) {
            const extra = await achievementService.processRecognitions(email, recs);
            unlocked.push(...extra);
          }
        }
        if (unlocked.length > 0) {
          setAchievementQueue((q) => [
            ...q,
            ...unlocked.filter((u) => !q.some((x) => x.id === u.id)),
          ]);
        }
      }
    })();
  }, [messages, userConfiguration]);

  // Trofeos por nivel (spec Excel Santiago): galia-ws emite los reconocimientos
  // del LLM como mensaje de control; webSocketService los reenvía en este
  // evento. Aquí (donde vive el email) se suman los contadores por casilla.
  // El último mensaje del estudiante se guarda en un ref para refinar las
  // casillas de localiser contra el texto literal (refineRecognitions).
  const lastUserMsgRef = useRef<string>('');
  useEffect(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user' && messages[i].content) {
        lastUserMsgRef.current = messages[i].content;
        break;
      }
    }
  }, [messages]);
  useEffect(() => {
    const email = trophyEmail();
    const isProfMode = userConfiguration?.mode === 'actividades-docente';
    if (!gamificationUI || !email || isProfMode) return; // solo estudiantes, solo build dev
    const onTrophees = (e: Event) => {
      let items = (e as CustomEvent).detail;
      if (!Array.isArray(items) || items.length === 0) return;
      // Los trofeos `local` ya no están en el prompt, pero si el modelo alucina
      // uno de sus marcadores igual, aquí se descarta: su única fuente es la
      // detección determinista (si contaran ambos, doblarían casillas de conteo).
      items = items.filter((i: { id: string }) => !TROPHIES_BY_ID[i.id]?.local);
      if (items.length === 0) return;
      items = refineRecognitions(items, lastUserMsgRef.current);
      achievementService.processRecognitions(email, items).then((unlocked) => {
        if (unlocked.length > 0) {
          setAchievementQueue((q) => [
            ...q,
            ...unlocked.filter((u) => !q.some((x) => x.id === u.id)),
          ]);
        }
      });
    };
    window.addEventListener('galia:trophees-reconnus', onTrophees);
    return () => window.removeEventListener('galia:trophees-reconnus', onTrophees);
  }, [gamificationUI, userConfiguration]);

  // Modo kiosco (pantalla de recepción AF): si hubo conversación y pasan 2
  // minutos sin actividad, se recarga la página — vuelve a la PORTADA con el
  // botón "Comenzar interacción" (?kiosk=1 se conserva), lista y en cero para
  // el siguiente visitante. 2 min (antes 4) porque ahora la portada es la
  // invitación: cuanto antes vuelva a ella, mejor vitrina.
  useEffect(() => {
    if (!isKioskMode()) return;
    if (!messages.some((m) => m.role === 'user')) return;
    const t = window.setTimeout(() => window.location.reload(), 2 * 60 * 1000);
    return () => window.clearTimeout(t);
  }, [messages]);

  // ¿Galia está hablando/respondiendo ahora? Controla el botón "Detener".
  const [isGaliaSpeaking, setIsGaliaSpeaking] = useState(false);
  const orientation = useScreenOrientation();
  const [showAcademicMode, setShowAcademicMode] = useState(false);
  const [academicLevel, setAcademicLevel] = useState<{ level: string; unit?: number; lesson?: number } | null>(null);
  const [isAcademicProgressVisible, setIsAcademicProgressVisible] = useState(false);
  const [isInformationVisible, setIsInformationVisible] = useState(false);
  const [isLessonInProgress, setIsLessonInProgress] = useState(false);
  const [showFeedbackBanner, setShowFeedbackBanner] = useState(false);
  const [isFeedbackBannerExpanded, setIsFeedbackBannerExpanded] = useState(false);
  
  // Estado para modal de racha de 5
  const [showStreak5Modal, setShowStreak5Modal] = useState(false);
  const [streak5Count, setStreak5Count] = useState(0);
  
  // Estados para sistema de actividades
  const [showTeacherActivityPanel, setShowTeacherActivityPanel] = useState(false);
  const [showTeacherActivityForm, setShowTeacherActivityForm] = useState(false);
  const [showTeacherActivityPreview, setShowTeacherActivityPreview] = useState(false);
  const [showStudentActivityList, setShowStudentActivityList] = useState(false);
  const [showStudentActivityView, setShowStudentActivityView] = useState(false);
  const [currentActivity, setCurrentActivity] = useState<TeacherActivity | null>(null);
  const [editingActivity, setEditingActivity] = useState<TeacherActivity | null>(null);
  
  // Estados para formulario de email cuando se accede por URL
  const [showActivityEmailForm, setShowActivityEmailForm] = useState(false);
  const [activityEmailError, setActivityEmailError] = useState<string | null>(null);
  const [activityEmailLoading, setActivityEmailLoading] = useState(false);

  // Acceso directo (fase 1): true = hay que pedir correo + código antes de nada;
  // null = todavía no se sabe (la sesión guardada se resuelve de forma asíncrona).
  // Ver docs/security/acceso-directo-fase1-plan.md
  const [directLoginNeeded, setDirectLoginNeeded] = useState<boolean | null>(
    isDirectAccessMode() ? null : false
  );

  // Sesión guardada vencida (acuerdo Santiago 01/09/2026: 1 semana). Arranca
  // con la lectura síncrona de localStorage para no mostrar la configuración un
  // instante antes de la pantalla de vencida; processAuth() lo corrige después
  // (p. ej. si el URL traía un ?session= nuevo que reemplaza la vencida). No
  // aplica al kiosco (sesión efímera) ni al acceso directo (tiene su puerta).
  const [sessionExpired, setSessionExpired] = useState<boolean>(
    () => !isKioskMode() && !isDirectAccessMode() && sessionService.peekStoredState() === 'expired'
  );

  // Panel de configuración persistente (chip + sidebar)
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);

  // Visor de contenidos interactivos Nomade (Geniallys de la AF)
  const [showContentViewer, setShowContentViewer] = useState(false);

  // Audio (TTS) de Galia activado/silenciado. Default: preferencia persistida del
  // estudiante (ver webSocketService). El modo profesor arranca silenciado.
  const [audioEnabled, setAudioEnabled] = useState<boolean>(() => webSocketService.getAudioEnabled());

  // Lee la preferencia de audio persistida del estudiante (default: activado).
  const readStudentAudioPref = (): boolean => {
    try { return localStorage.getItem('galia_audio_enabled') !== 'false'; } catch { return true; }
  };

  // Alternar audio desde el botón de altavoz. En modo profesor no se persiste
  // (silenciado es el default por sesión); para estudiantes sí se guarda.
  const handleToggleAudio = () => {
    const next = !audioEnabled;
    const isProf = userConfiguration?.mode === 'actividades-docente';
    webSocketService.setAudioEnabled(next, !isProf);
    setAudioEnabled(next);
  };

  // "Nueva conversación" (pedido Diana Soler, reunión 2026-07-21): reinicia el
  // hilo con Galia para repetir un ejercicio desde cero. La conexión WS se
  // conserva; el guardián de resume evita que una recarga < 24h rehidrate el
  // hilo que el estudiante acaba de descartar.
  const handleResetConversation = async () => {
    markConversationReset();
    await webSocketService.resetConversation();
    setIsGaliaSpeaking(false);
    setHablando(false);
    void refreshRecentConversations();
  };

  // Pestañas de conversaciones recientes (acuerdo Santiago 01/09/2026): las
  // últimas 3 del mismo modo, sin vigencia, arriba del cuadro de chat en lugar de
  // los chips. Los datos salen de Supabase (ver recentConversationsService).
  const [recentConversations, setRecentConversations] = useState<RecentConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string>('');
  const refreshRecentConversations = async () => {
    const email = configService.getConfig().email || '';
    const mode = configService.getConfig().practiceMode;
    setActiveConversationId(conversationSaverService.getCurrentInteractionId());
    if (!email || isKioskMode()) {
      setRecentConversations([]);
      return;
    }
    const list = await getRecentConversations(email, mode);
    setRecentConversations(list);
    setActiveConversationId(conversationSaverService.getCurrentInteractionId());
  };
  const handleSelectConversation = async (id: string) => {
    if (id === conversationSaverService.getCurrentInteractionId()) return;
    const target = recentConversations.find((c) => c.id === id);
    if (!target) return;
    await webSocketService.switchConversation(target.messages, target.id, target.previousTimeTotalSeconds);
    setIsGaliaSpeaking(false);
    setHablando(false);
    void refreshRecentConversations();
  };

  const chatTabs: ChatTab[] = recentConversations.map((c) => ({
    id: c.id,
    label: formatTabLabel(c.lastActivityAt, userConfiguration?.mode === 'actividades-docente' ? 'fr' : 'es'),
    preview: c.preview,
  }));

  // Refrescar pestañas al entrar a la interacción y cada vez que se abre el
  // chat (barato: una consulta a Supabase, falla abierto).
  useEffect(() => {
    if (!initialConfigComplete || !userConfiguration) return;
    if (!isChatVisible) return;
    void refreshRecentConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfigComplete, userConfiguration?.mode, isChatVisible]);

  // Estados para el tracker de objetivos compacto
  const [showObjectivesPanel, setShowObjectivesPanel] = useState(false);
  const [currentLessonObjectives, setCurrentLessonObjectives] = useState<any[]>([]);
  const [currentLessonProgress, setCurrentLessonProgress] = useState<any | null>(null);

  // Email efectivo para InitialScreen: evita que loadUserData (racha, tiempos, interacciones) corra con email vacío
  const [effectiveEmailForInitial, setEffectiveEmailForInitial] = useState<string | null>(null);

  // Mostrar banner de feedback cuando hay una conversación guardable
  // No mostrar si ya se envió feedback en esta sesión
  useEffect(() => {
    const feedbackSent = sessionStorage.getItem('feedback_sent_this_session') === 'true';
    if (initialConfigComplete && messages.length > 0 && messages.some(m => m.role === 'user') && !feedbackSent) {
      setShowFeedbackBanner(true);
    } else if (feedbackSent) {
      setShowFeedbackBanner(false);
    }
  }, [initialConfigComplete, messages]);


  // Verificar si hay una lección en proceso
  const checkLessonInProgress = async () => {
    try {
      const config = configService.getConfig();
      if (!config.email || !academicLevel) {
        setIsLessonInProgress(false);
        return;
      }
      
      // Verificar si el modo académico está activo en WebSocket
      // Esto indica que se inició una lección
      const academicModeActive = (webSocketService as any).academicMode?.isActive;
      
      if (academicModeActive) {
        setIsLessonInProgress(true);
        
        // Cargar objetivos y progreso de la lección activa
        const academicMode = (webSocketService as any).academicMode;
        if (academicMode.level && academicMode.unit !== undefined && academicMode.lesson !== undefined) {
          const objectives = await academicService.getLessonObjectives(
            academicMode.level,
            academicMode.unit,
            academicMode.lesson
          );
          const progress = await academicService.getUserLessonProgress(
            config.email,
            academicMode.level,
            academicMode.unit,
            academicMode.lesson
          );
          
          setCurrentLessonObjectives(objectives);
          setCurrentLessonProgress(progress);
        }
      } else {
        // También verificar si hay progreso en la lección actual sin completar
        const progress = await academicService.getUserLessonProgress(
          config.email,
          academicLevel.level,
          academicLevel.unit || 0,
          academicLevel.lesson || 1
        );
        
        if (progress && !progress.all_objectives_completed) {
          // Verificar si hay algún objetivo con progreso
          const hasProgress = Object.values(progress.objectives_status).some(
            (status: any) => status.correct_count > 0
          );
          setIsLessonInProgress(hasProgress);
          
          if (hasProgress) {
            // Cargar objetivos para mostrar en el tracker
            const objectives = await academicService.getLessonObjectives(
              academicLevel.level,
              academicLevel.unit || 0,
              academicLevel.lesson || 1
            );
            setCurrentLessonObjectives(objectives);
            setCurrentLessonProgress(progress);
          }
        } else {
          setIsLessonInProgress(false);
          setCurrentLessonObjectives([]);
          setCurrentLessonProgress(null);
        }
      }
    } catch {
      setIsLessonInProgress(false);
      setCurrentLessonObjectives([]);
      setCurrentLessonProgress(null);
    }
  };

  // Handler para cambios de visibilidad del chat
  const handleChatVisibilityChange = (isVisible: boolean) => {
    setIsChatVisible(isVisible);
    conversationSaverService.setChatVisibility(isVisible);
  };

  // Modo profesor (aide-prof): adjuntar un documento. Se extrae el texto en el
  // navegador (no se sube a ningún servidor) y se inyecta en el contexto de
  // sistema del WebSocket. No se envía ningún mensaje a Galia: el profesor
  // escribe después qué quiere hacer con el documento (pedido Santiago 13/08).
  const handleAttachDocument = async (file: File): Promise<{ ok: boolean; error?: string }> => {
    try {
      const { text, empty, filename } = await extractTextFromFile(file);
      if (empty) {
        return { ok: false, error: 'No se obtuvo texto del documento (¿PDF escaneado?). Usa un PDF con texto o un .txt.' };
      }
      webSocketService.setTeacherDocument(text, filename);
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message || 'No se pudo procesar el documento.' };
    }
  };

  // Modo profesor (aide-prof): co-construir con Galia a partir del contenido que
  // el profesor está viendo en el visor « Contenus » (un episodio Nomade o un
  // libro de Cours internes). Lleva esa selección al chat como un mensaje, cierra
  // el visor y abre la conversación.
  const handleWorkWithContent = (message: string) => {
    setShowContentViewer(false);
    handleChatVisibilityChange(true);
    void sendTextMessage(message);
  };

  // En modo vertical (portrait), solo cerrar el chat cuando el usuario ABRE un panel (no cuando abre el chat ni por cambios breves de orientación en móvil)
  const prevPanelOpenRef = useRef(false);
  useEffect(() => {
    if (orientation !== 'portrait') {
      // No resetear prevPanelOpenRef en landscape: en móvil el teclado/resize puede cambiar orientation brevemente y cerrar el chat al volver
      return;
    }
    const panelOpen = showAcademicMode || showTeacherActivityPanel || showStudentActivityList || showStudentActivityView;
    // Solo cerrar el chat cuando un panel pasa de cerrado a abierto (el usuario abrió un panel), no cuando el usuario abre el chat
    if (panelOpen && !prevPanelOpenRef.current && isChatVisible) {
      handleChatVisibilityChange(false);
    }
    prevPanelOpenRef.current = panelOpen;
    // No incluir isChatVisible en deps: si lo incluimos, al abrir el chat el efecto se dispara y puede cerrarlo (p. ej. si prevPanelOpenRef quedó false por un resize en móvil)
  }, [orientation, showAcademicMode, showTeacherActivityPanel, showStudentActivityList, showStudentActivityView]);

  useEffect(() => {
    // Inicialización inmediata para que la app cargue al abrir local (no depender de loadPolyfills)
    const envTestingPhase = import.meta.env.VITE_TESTING_PHASE;
    const testingPhase = envTestingPhase !== undefined
      ? (envTestingPhase === 'true' || envTestingPhase === true)
      : (import.meta.env.DEV === true);

    configService.initialize({
      testingPhase: testingPhase,
      testingWebSocket: true,
      useApolearn: true,
      client: 'alianzafrancesa',
      assistantID: 'f613066c-a9ba-47b5-b2db-1d82b38366ed',
      promptReference: `alianzafrancesa-galia-demo-${testingPhase ? 'dev' : 'pro'}`,
    });

    const urlParamsInit = new URLSearchParams(window.location.search);
    const hasTokenInit = urlParamsInit.has('token') || window.location.href.includes('token=');
    const configInit = configService.getConfig();
    // Sin token LTI no se configuran valores por defecto; el correo se pide por formulario y debe ser de Apolearn
    const emailForInitial = configService.getConfig().email || null;
    setEffectiveEmailForInitial(emailForInitial);
    setIsInitialized(true);

    // Polyfills y resto de la lógica (tache_id, LTI, etc.) sin bloquear la carga
    loadPolyfills().then(async () => {
      // Fases 2-3 seguridad LTI: la identidad viene del token de sesión firmado
      // (?session= canjeado en el servidor, o la sesión guardada de la PWA);
      // los parámetros planos email/roles/user_id del URL ya no se aceptan.
      const authPayload = await authService.processAuth();

      // Sesión vencida: solo si tras resolver NO quedó identidad viva. Las URL
      // de actividad (tache_id) siguen su propio camino con formulario de correo.
      if (!isKioskMode() && !isDirectAccessMode()) {
        const hasTache = new URLSearchParams(window.location.search).has('tache_id');
        setSessionExpired(!hasTache && !sessionService.getIdentity() && !!sessionService.getExpiredIdentity());
      }

      // Acceso directo (fase 1): sin sesión resuelta hay que pedir correo +
      // código. Con sesión guardada (visita anterior, 7 días) se sigue de
      // largo — la puerta directa no vuelve a preguntar cada vez.
      if (isDirectAccessMode()) {
        const known = sessionService.getIdentity();
        if (known?.email) {
          configService.setUserInfo(known.email, known.full_name || '');
          setEffectiveEmailForInitial(known.email);
          setDirectLoginNeeded(false);
        } else {
          setDirectLoginNeeded(true);
        }
      }

      const urlParams = new URLSearchParams(window.location.search);
      const tacheId = urlParams.get('tache_id');
      if (tacheId) {
        // Si hay tache_id, cargar la actividad. Solo pedir email si aún no hay uno en esta sesión.
        teacherActivityService.getActivityByTacheId(tacheId).then((activity) => {
          if (activity) {
            setCurrentActivity(activity);
            const configForActivity = configService.getConfig();
            if (!configForActivity.email) {
              setShowActivityEmailForm(true);
            } else {
              // Email ya configurado en esta sesión: no pedir de nuevo, abrir actividad directamente
              setShowActivityEmailForm(false);
              setShowStudentActivityView(true);
              setShouldConnect(true);
              setInitialConfigComplete(true);
              const isTestingPhase = configForActivity.testingPhase ?? false;
              const promptRef = getPromptReferenceWithRole('actividades-estudiante', null, isTestingPhase);
              configService.setPromptReference(promptRef);
              configService.setAvatarType('actividades-estudiante');
              configService.setPracticeModeAndAccent('actividades-estudiante', 'Francia');
              // Sprint Galia 1.5: acento europeo unico (ver src/config/voiceConfig.ts)
              const voiceInfo = { voice: 'fr-FR-Chirp3-HD-Achernar', language: 'fr-FR' };
              setUserConfiguration({
                mode: 'actividades-estudiante',
                promptReference: promptRef,
                rasgo: 'Neutral',
                personalityType: 'Neutral',
                functionalityType: 'Conversadora',
                mcerLevel: 'A1',
                acento: 'Francia',
                voice: voiceInfo.voice,
                language: voiceInfo.language,
              });
              conversationSaverService.setCurrentConfiguration('actividades-estudiante', 'Neutral', 'Francia');
              conversationSaverService.setChatVisibility(false);
            }
          }
        }).catch(() => {
          setActivityEmailError('No se pudo cargar la actividad. Verifica la URL.');
        });
      }

      // Verificar si hay token de acceso Apolearn en la sesión (LTI 1.1 no lo envía)
      const hasToken = !!authPayload?.token;
      const hasApolearnData = authService.getApolearnUserData();
      const config = configService.getConfig();
      // Sin token LTI el email se pide por formulario; si no es de Apolearn no se permite ingreso

      // Sincronizar email con estado para que InitialScreen reciba el email y cargue racha/tiempos/interacciones
      const currentEmail = configService.getConfig().email || null;
      setEffectiveEmailForInitial(currentEmail);

      // Si viene de Apolearn (con o sin token), esperar a que se obtengan los datos completos.
      // Apolearn LTI 1.1 no envía token propio — basta con email o user_id para identificar al estudiante.
      // Acceso directo (origin 'direct'): NO hay datos en Apolearn que pedir — el
      // nivel viene en el claim `nivel` (getStudentContentLevel lo usa) y la SPA
      // no debe tocar el proxy de Apolearn con un token directo (plan fase 1 §5).
      const isDirectSession = sessionService.getIdentity()?.origin === 'direct';
      if (authPayload?.email && !isDirectSession) {
        // Intentar obtener datos completos si aún no están disponibles
        // Usar user_id de LTI Launch si está disponible
        const ltiUserId = parseInt(authPayload.user_id || '0') || 0;
        authService.getApolearnCompleteData(authPayload.email, ltiUserId > 0 ? ltiUserId : undefined)
          .then(apolearnData => {
            
            // Ahora que tenemos los datos completos, intentar obtener el nivel académico
            const config = configService.getConfig();
            const emailToUse = apolearnData.user?.email || authPayload.email || config.email;
            
            if (apolearnData && emailToUse) {
              academicService.getUserAcademicLevel().then(async (level) => {
                if (level) {
                  setAcademicLevel(level);
                  setShowAcademicMode(true);
                  // Verificar si hay una lección en proceso
                  await checkLessonInProgress();
                }
              }).catch(() => {});
            }
          })
          .catch(() => {
            // Intentar con los datos mínimos si están disponibles
            const apolearnData = authService.getApolearnUserData();
            if (apolearnData && apolearnData.user?.email) {
              const config = configService.getConfig();
              const emailToUse = apolearnData.user.email || config.email;
              if (emailToUse) {
                academicService.getUserAcademicLevel().then(async (level) => {
                  if (level) {
                    setAcademicLevel(level);
                    setShowAcademicMode(true);
                  }
                }).catch(() => {});
              }
            }
          });
      }

      // Verificar si hay datos de Apolearn y activar modo académico (para casos sin token)
      const apolearnData = authService.getApolearnUserData();
      // Reutilizar config ya declarado arriba
      
      // Si hay datos de Apolearn pero el email en config no coincide, actualizarlo
      // PRIORIDAD: El email de LTI siempre tiene prioridad sobre cualquier otro email
      if (apolearnData && apolearnData.user?.email) {
        // Si el email en config no coincide con el email de Apolearn, actualizarlo
        if (config.email !== apolearnData.user.email) {
          configService.setUserInfo(
            apolearnData.user.email,
            `${apolearnData.user.firstname || ''} ${apolearnData.user.lastname || ''}`.trim(),
            apolearnData.user.id,
            apolearnData.user.firstname,
            apolearnData.user.lastname
          );
        }
      } else if (authPayload?.email) {
        // Si no hay datos completos de Apolearn pero hay email de LTI, usarlo
        if (config.email !== authPayload.email) {
          configService.setUserInfo(
            authPayload.email,
            authPayload.full_name || '',
            undefined,
            authPayload.full_name?.split(' ')[0],
            authPayload.full_name?.split(' ').slice(1).join(' ')
          );
        }
      }
      
      const updatedConfig = configService.getConfig();
      // Usar el email de Apolearn si está disponible, sino el de LTI, sino el de config
      const emailToUse = apolearnData?.user?.email || authPayload?.email || updatedConfig.email;
      
      // Solo intentar obtener nivel académico si NO hay token y ya hay datos (ya que si hay token, se maneja arriba)
      if (!hasToken && apolearnData && emailToUse) {
        // Obtener nivel académico desde Apolearn
        academicService.getUserAcademicLevel().then(async (level) => {
          if (level) {
            setAcademicLevel(level);
            setShowAcademicMode(true);
            // Verificar si hay una lección en proceso
            await checkLessonInProgress();
          }
        }).catch(() => {});
      }

      // Activar utilidades de prueba (solo en desarrollo)
      if (testingPhase) {
        enableTestAcademicMode();
        
        // Función rápida para activar modo académico de prueba
        if (typeof window !== 'undefined') {
          (window as any).quickTestAcademic = () => {
            setAcademicLevel({ level: 'A1.1', unit: 0, lesson: 1 });
            setShowAcademicMode(true);
          };
          
          /**
           * ACTIVAR MODO ACADÉMICO MANUALMENTE
           * 
           * Esta función activa el modo académico después de simular datos de Apolearn.
           * Se usa cuando ejecutas window.simulateApolearnA1_1() y quieres ver el modo académico
           * sin recargar la página.
           * 
           * Para usar: window.activateAcademicMode()
           */
          (window as any).activateAcademicMode = async () => {
            const apolearnData = authService.getApolearnUserData();
            const config = configService.getConfig();if (!apolearnData) {
              console.error('❌ No hay datos de Apolearn. Usa window.simulateApolearnA1_1() primero.');
              return;
            }
            
            if (!config.email) {
              console.error('❌ No hay email configurado. Configura el email primero.');
              return;
            }
            
            try {const level = await academicService.getUserAcademicLevel();if (level) {setAcademicLevel(level);
                setShowAcademicMode(true);
              } else {
                console.error('❌ No se pudo obtener el nivel académico');
              }
            } catch (error) {console.error('❌ Error al activar modo académico:', error);
            }
          };
          
          /**
           * DESACTIVAR MODO ACADÉMICO
           * 
           * Esta función desactiva el modo académico y oculta el componente.
           * 
           * Para usar: window.deactivateAcademicMode()
           */
          (window as any).deactivateAcademicMode = () => {
            setShowAcademicMode(false);
            setAcademicLevel(null);
          };
        }
      }

        // TEST: Probar obtención de datos de Apolearn con email específico
        // Ejecutar desde consola: window.testApolearn('bryan@lidarit.com')
        if (typeof window !== 'undefined') {
          (window as any).testApolearn = async (email: string) => {
            try {
              const data = await authService.getApolearnCompleteData(email);
              return data;
            } catch (error: any) {
              throw error;
            }
          };
          
          // TEST: Probar conexión con Supabase
          (window as any).testSupabase = async () => {
            try {
              const success = await supabaseService.testConnection();
              return success;
            } catch (error: any) {
              throw error;
            }
          };
          
          // Función para imprimir estadísticas de todos los classrooms del usuario
          // Ejecutar desde consola: window.printClassroomStatistics()
          (window as any).printClassroomStatistics = async () => {
            try {
              await authService.printAllClassroomStatistics();
            } catch (error: any) {
              console.error('Error al imprimir estadísticas:', error);
            }
          };
          
          // Probar conexión automáticamente en modo desarrollo
          if (testingPhase) {
            supabaseService.testConnection().catch(() => {});
          }
        }

      // Las preferencias no se guardan en localStorage por ahora
      // El menú siempre aparecerá al inicio

      // Inicializar tiempo de interacción
      interactionTimeService.initialize();

      // Configurar callbacks del WebSocket
      webSocketService.onAvatarState((isHablando) => {
        setHablando(isHablando);
        setIsGaliaSpeaking(isHablando);
      });

      // Inicializar handler de cierre del navegador
      browserCloseHandler.initialize();

      setEffectiveEmailForInitial(configService.getConfig().email || null);
      setIsInitialized(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar

  // Guardar conversación periódicamente
  useEffect(() => {
    if (messages.length > 0) {
      const interval = setInterval(() => {
        conversationSaverService.guardarData(
          messages.map((m) => ({ role: m.role, content: m.content, afterInvalidMessage: m.afterInvalidMessage }))
        );
      }, 30000); // Cada 30 segundos

      return () => clearInterval(interval);
    }
  }, [messages]);

  // Actualizar progreso de objetivos en tiempo real cuando hay una lección activa
  useEffect(() => {
    if (!isLessonInProgress || !academicLevel) {
      setCurrentLessonObjectives([]);
      setCurrentLessonProgress(null);
      return;
    }

    const updateProgress = async () => {
      try {
        const config = configService.getConfig();
        if (!config.email) return;

        const academicMode = (webSocketService as any).academicMode;
        if (academicMode?.isActive) {
          const progress = await academicService.getUserLessonProgress(
            config.email,
            academicMode.level,
            academicMode.unit,
            academicMode.lesson
          );
          setCurrentLessonProgress(progress);
        }
      } catch {
        // Error silencioso al actualizar progreso
      }
    };

    // Actualizar cada 2 segundos
    const interval = setInterval(updateProgress, 2000);
    
    return () => clearInterval(interval);
  }, [isLessonInProgress, academicLevel]);

  const handleRecordingComplete = async (audioBlob: Blob) => {
    try {
      // Activar animación de avatar hablando
      setHablando(true);

      // Enviar audio a WebSocket
      await sendAudioMessage(audioBlob);

      // La animación se desactivará cuando termine la respuesta
      // (esto se manejará en el servicio de WebSocket)
    } catch (error) {
      console.error('Error al procesar grabación:', error);
      setHablando(false);
    }
  };

  if (!isInitialized) {
    return (
      <div className="loading-screen">
        <p>Cargando...</p>
      </div>
    );
  }

  // Acceso directo (fase 1): la puerta por correo va ANTES que todo lo demás.
  // `null` = aún resolviendo la sesión guardada; mostrar el formulario en ese
  // hueco haría parpadear el login a quien ya tiene sesión.
  if (directLoginNeeded === null) {
    return (
      <div className="loading-screen">
        <p>Cargando...</p>
      </div>
    );
  }
  if (directLoginNeeded) {
    return (
      <DirectLogin
        onAuthenticated={(identity: SessionIdentity) => {
          // El token ya quedó guardado por sessionService.adopt(); aquí solo se
          // rellena lo que el resto de la app lee de configService. No se llama
          // a Apolearn: en esta puerta el nivel y el nombre vienen de AEC.
          if (identity.email) {
            configService.setUserInfo(identity.email, identity.full_name || '');
            setEffectiveEmailForInitial(identity.email);
          }
          setDirectLoginNeeded(false);
        }}
      />
    );
  }

  // Sesión vencida: bloquea la app hasta que un lanzamiento nuevo por Apolearn
  // reescriba la sesión guardada (la pantalla recarga sola al detectarla).
  if (sessionExpired) {
    return <SessionExpired identity={sessionService.peekExpiredIdentity()} />;
  }

  const handleReturnToMenu = () => {
    // Guardar la configuración actual antes de resetearla
    if (userConfiguration) {
      previousConfigurationRef.current = userConfiguration;
    }
    // NO desconectar el WebSocket ni cambiar shouldConnect
    // Solo ocultar el menú para permitir que el usuario seleccione elementos nuevamente
    // El WebSocket permanecerá conectado hasta que se presione "Aceptar" con una configuración diferente.
    // PERO sí limpiar colas de audio/mensajes en tránsito: sino, Galia sigue
    // hablando mientras el usuario ya ve el InitialScreen otra vez.
    webSocketService.clearAllQueues();
    setInitialConfigComplete(false);
    setUserConfiguration(null);
    setShowActivityEmailForm(false); // Resetear el formulario de email si estaba activo
    setCurrentActivity(null); // Limpiar la actividad actual
    setShowStudentActivityView(false); // Cerrar la vista de actividad
    setShowStudentActivityList(false); // Cerrar la lista de actividades
    setShowTeacherActivityPanel(false); // Cerrar el panel de actividades del profesor
    // Mantener shouldConnect como está - NO cambiar a false
    // NO llamar a webSocketService.disconnect()
  };

  // Handler para el formulario de email cuando se accede por URL
  const handleActivityEmailSubmit = async (email: string) => {
    setActivityEmailLoading(true);
    setActivityEmailError(null);

    try {
      // Llamar a getApolearnCompleteData con solo el email (sin userId)
      // Esto obtendrá el usuario por email y luego usará su ID para obtener los datos completos
      const apolearnData = await authService.getApolearnCompleteData(email);
      
      if (!apolearnData || !apolearnData.user) {
        throw new Error('No se pudieron obtener los datos del usuario');
      }

      // Obtener el userId del objeto user retornado
      const userId = apolearnData.user.id;
      
      // Configurar configService con email y userId
      configService.setUserInfo(
        email,
        `${apolearnData.user.firstname || ''} ${apolearnData.user.lastname || ''}`.trim(),
        userId,
        apolearnData.user.firstname,
        apolearnData.user.lastname
      );

      // Obtener role de los datos de Apolearn (puede ser null/undefined)
      const role = apolearnData.role || null;
      const isTestingPhase = configService.getConfig().testingPhase || false;

      // Determinar promptReference (funciona correctamente incluso sin role)
      const promptReference = getPromptReferenceWithRole('actividades-estudiante', role, isTestingPhase);

      // Sprint Galia 1.5: acento europeo unico (ver src/config/voiceConfig.ts)
      const accent = 'Francia';
      const voiceInfo = { voice: 'fr-FR-Chirp3-HD-Achernar', language: 'fr-FR' };

      // Configurar automáticamente el modo actividades-estudiante
      const autoConfig: UserConfiguration = {
        mode: 'actividades-estudiante',
        promptReference: promptReference,
        rasgo: 'Neutral',
        personalityType: 'Neutral',
        functionalityType: 'Conversadora',
        mcerLevel: 'A1',
        acento: accent,
        voice: voiceInfo.voice,
        language: voiceInfo.language,
      };

      // Configurar servicios
      configService.setPromptReference(promptReference);
      configService.setAvatarType(autoConfig.mode);
      configService.setPracticeModeAndAccent(autoConfig.mode, autoConfig.acento);
      conversationSaverService.setCurrentConfiguration(autoConfig.mode, autoConfig.rasgo, autoConfig.acento);
      conversationSaverService.setChatVisibility(false);

      // Guardar configuración y completar
      setUserConfiguration(autoConfig);
      setInitialConfigComplete(true);

      // Ocultar formulario y mostrar actividad
      setShowActivityEmailForm(false);
      setShowStudentActivityView(true);

      // Activar conexión WebSocket
      setShouldConnect(true);
    } catch {
      setActivityEmailError('Verifica que sea tu correo de apolearn');
    } finally {
      setActivityEmailLoading(false);
    }
  };

  const handleConfigurationComplete = async (userConfig: UserConfiguration) => {
    // iOS Safari: desbloquear audio ANTES de cualquier await. Esto es crítico
    // porque estamos dentro del gesto del tap del botón "Iniciar"; si esperamos
    // un await primero, iOS pierde el contexto de gesto y bloquea el TTS inicial.
    unlockAudioPlayback();

    logger.log('[WS-DIAG] handleConfigurationComplete INICIO', { mode: userConfig.mode, rasgo: userConfig.rasgo, acento: userConfig.acento });
    // Resetear el estado de primera interacción para que la próxima vez muestre greeting
    resetFirstInteraction();
    
    // Usar userConfiguration si está disponible, sino usar previousConfigurationRef
    const configToCompare = userConfiguration || previousConfigurationRef.current;

    // Default de audio según el MODO al entrar a uno nuevo:
    // - Modo profesor (aide-prof): silenciado por defecto (es un asistente para
    //   preparar clases, no para escuchar); preferencia de sesión, no persistida.
    // - Estudiante: respetar su preferencia persistida.
    // Solo se aplica cuando cambia el modo, para no pisar el toggle del usuario al
    // reaplicar la misma configuración (p. ej. cambiar solo personalidad).
    if (configToCompare?.mode !== userConfig.mode) {
      if (userConfig.mode === 'actividades-docente') {
        webSocketService.setAudioEnabled(false, false);
        setAudioEnabled(false);
      } else {
        const pref = readStudentAudioPref();
        webSocketService.setAudioEnabled(pref, false);
        setAudioEnabled(pref);
      }
    }

    // Comparar con la configuración anterior para determinar si es la misma.
    // IMPORTANTE: incluir mcerLevel y functionalityType. Si solo cambian estos,
    // el promptReference no se modifica (es el mismo "mode"), pero el prompt
    // ensamblado SI cambia (incluye nivel/funcionalidad), asi que necesitamos
    // reconectar el WS para regenerarlo en el backend.
    const isSameConfiguration = configToCompare &&
      configToCompare.mode === userConfig.mode &&
      configToCompare.rasgo === userConfig.rasgo &&
      configToCompare.acento === userConfig.acento &&
      configToCompare.mcerLevel === userConfig.mcerLevel &&
      configToCompare.functionalityType === userConfig.functionalityType;

    // Cambios que afectan el ESTILO de Galia (no solo la voz). Si cambia alguno,
    // el chathistory previo "ancla" al modelo al estilo anterior aunque el prompt
    // se reensamble — el LLM hace in-context learning sobre los turnos pasados.
    // Por eso al cambiar nivel/personalidad/funcionalidad/modo descartamos resume
    // y limpiamos historial: Galia arranca conversacion nueva con prompt nuevo.
    // Si solo cambia el acento (cuando haya >1), preservamos el historial.
    const styleAffectingChange = configToCompare && !isSameConfiguration && (
      configToCompare.mode !== userConfig.mode ||
      configToCompare.mcerLevel !== userConfig.mcerLevel ||
      configToCompare.functionalityType !== userConfig.functionalityType ||
      configToCompare.rasgo !== userConfig.rasgo
    );

    // HILO ÚNICO (pedido del cliente Santiago Escobar): la conversación es un
    // único hilo continuo. Al cambiar personalidad, nivel, funcionalidad o acento
    // NO se reinicia la charla — se mantiene el historial y Galia solo ajusta su
    // comportamiento de aquí en adelante. Como el backend es stateless (cada
    // mensaje reenvía [{system: prompt}, ...historial] vía buildFullContext), al
    // reconectar se reensambla el prompt nuevo y viaja junto al historial intacto.
    // Solo un cambio de MODO inicia un hilo nuevo (el modo académico trae tarea/
    // objetivos propios y es un contexto distinto).
    const keepThreadOnBehaviorChange = !!configToCompare && !isSameConfiguration &&
      configToCompare.mode === userConfig.mode;

    const hasPreviousConfig = !!configToCompare;

    // Resume: si la última conversación del estudiante tuvo actividad < 24h y
    // tanto el modo previo como el nuevo son retomar-friendly (conversacional/
    // estudio), reusamos su internal_interaction_id y rehidratamos chathistory.
    // Si retorna null, seguimos el flujo de "conversación nueva" como hasta hoy.
    const emailForResume = configService.getConfig().email || '';
    const resume = await tryResumeConversation(emailForResume, userConfig.mode);

    // Si la configuración es diferente Y hay una configuración previa, guardar datos anteriores y limpiar
    if (!isSameConfiguration && hasPreviousConfig) {
      // Obtener mensajes actuales antes de limpiar
      const currentMessages = webSocketService.getChatHistory();

      // Si hay mensajes, enviarlos al lambda antes de limpiar
      if (currentMessages.length > 0) {
        const finalMessages = currentMessages.map((m) => ({
          role: m.role,
          content: m.content,
          afterInvalidMessage: m.afterInvalidMessage,
        }));

        try {
          await conversationSaverService.guardarData(finalMessages);
        } catch {
          // Error silencioso al guardar datos anteriores
        }
      }

      // Limpiar todas las colas de audio y mensajes pendientes
      webSocketService.clearAllQueues();

      if (keepThreadOnBehaviorChange) {
        // HILO ÚNICO: cambió personalidad/nivel/funcionalidad/acento pero NO el
        // modo. Mantenemos el chathistory vivo y la MISMA fila de conversación en
        // Supabase (no reseteamos el estado): la conversación continúa y Galia solo
        // cambia su comportamiento de aquí en adelante. El prompt nuevo ya fue
        // invalidado por los setters de configService y se reensambla al reconectar;
        // viaja en cada mensaje vía buildFullContext.
        // Salvaguarda: si por alguna razón no hay historial vivo en memoria (p.ej.
        // recarga de página) pero hay una conversación retomable, la rehidratamos
        // para no perder el hilo.
        if (webSocketService.getChatHistory().length === 0 && resume) {
          webSocketService.loadChatHistory(resume.messages);
          conversationSaverService.restoreConversationState(
            resume.internalInteractionId,
            resume.alreadyPersistedCount,
            resume.previousTimeTotalSeconds,
          );
        }
        // Opción B: recordatorio de tono. Como conservamos el historial, el LLM
        // tiende a seguir el estilo previo por in-context learning sobre ~decenas
        // de miles de chars. Inyectamos un turno enfático (vía buildFullContext)
        // para que el cambio de personalidad/funcionalidad/nivel se note ya, sin
        // romper el hilo. Solo aplica al primer turno tras el cambio.
        const cambios: string[] = [];
        if (configToCompare?.rasgo !== userConfig.rasgo) {
          cambios.push(`tu personalidad ahora es "${userConfig.rasgo}"`);
        }
        if (configToCompare?.functionalityType !== userConfig.functionalityType) {
          cambios.push(`tu funcionalidad ahora es "${userConfig.functionalityType}"`);
        }
        if (configToCompare?.mcerLevel !== userConfig.mcerLevel) {
          cambios.push(`el nivel del estudiante ahora es "${userConfig.mcerLevel}"`);
        }
        if (cambios.length > 0) {
          webSocketService.setBehaviorReminder(
            `[INSTRUCTION SYSTÈME — changement de configuration] À partir de MAINTENANT, ${cambios.join(', ')}. ` +
            `Applique ce nouveau style et ce nouveau niveau DÈS ta prochaine réponse, ` +
            `même si les tours précédents de la conversation suivaient un autre ton ou un autre niveau. ` +
            `Ignore le style des tours antérieurs : seule cette configuration compte désormais. ` +
            `Ne commente pas ce changement, continue la conversation naturellement avec le nouveau comportement.`
          );
        }
        logger.log('[Config] Cambio de comportamiento — se mantiene el hilo único', {
          from: { rasgo: configToCompare?.rasgo, level: configToCompare?.mcerLevel, func: configToCompare?.functionalityType, acento: configToCompare?.acento },
          to: { rasgo: userConfig.rasgo, level: userConfig.mcerLevel, func: userConfig.functionalityType, acento: userConfig.acento },
          messageCount: webSocketService.getChatHistory().length,
          behaviorReminder: cambios,
        });
      } else if (resume && !styleAffectingChange) {
        // Retomar: reemplazar el chathistory por el persistido y reusar el GUID
        // para que los próximos guardarData hagan UPDATE de la misma fila en
        // Supabase en vez de crear una nueva. Solo cuando el cambio NO afecta
        // el estilo (p.ej. solo acento) — sino el modelo se queda en el patron
        // viejo por in-context learning sobre el historial.
        webSocketService.loadChatHistory(resume.messages);
        conversationSaverService.restoreConversationState(
          resume.internalInteractionId,
          resume.alreadyPersistedCount,
          resume.previousTimeTotalSeconds,
        );
        logger.log('[Resume] Retomando conversación previa', {
          id: resume.internalInteractionId,
          messageCount: resume.messages.length,
          mode: resume.mode,
          ageMinutes: Math.round((Date.now() - resume.lastActivityAt) / 60000),
        });
      } else {
        // Cambio de MODO (o sin resume): limpiar historial y arrancar conversación
        // nueva. El prompt nuevo se aplica sin "ruido" del contexto previo.
        webSocketService.clearChatHistory();
        conversationSaverService.resetConversationState();
        if (styleAffectingChange) {
          logger.log('[Config] Cambio de modo detectado — limpiando chathistory para evitar arrastre', {
            from: { mode: configToCompare?.mode, level: configToCompare?.mcerLevel, rasgo: configToCompare?.rasgo, func: configToCompare?.functionalityType },
            to: { mode: userConfig.mode, level: userConfig.mcerLevel, rasgo: userConfig.rasgo, func: userConfig.functionalityType },
          });
        }
      }

      // Desconectar WebSocket para reiniciar con nueva configuración
      webSocketService.disconnect();
      setShouldConnect(false);

      // Esperar un poco para que la desconexión se complete antes de continuar
      await new Promise(resolve => setTimeout(resolve, 300));
    } else if (!hasPreviousConfig && resume) {
      // Primera entrada de la sesión y hay una conversación retomar-friendly.
      // Hidratamos antes de conectar: useWebSocket sincroniza messages desde
      // chathistory cuando shouldConnect pasa a true (ver useWebSocket.ts:99-102).
      webSocketService.loadChatHistory(resume.messages);
      conversationSaverService.restoreConversationState(
        resume.internalInteractionId,
        resume.alreadyPersistedCount,
        resume.previousTimeTotalSeconds,
      );
      logger.log('[Resume] Retomando conversación en primera entrada', {
        id: resume.internalInteractionId,
        messageCount: resume.messages.length,
        mode: resume.mode,
        ageMinutes: Math.round((Date.now() - resume.lastActivityAt) / 60000),
      });
    }

    // Si la configuración no cambió, no volver a setear promptReference ni reconectar
    if (isSameConfiguration) {
      // Si la página se recargó y el chathistory está vacío pero hay una
      // conversación retomar-friendly < 24h en Supabase, hidratamos antes de
      // (re)conectar para que el alumno vea sus mensajes previos.
      if (resume && webSocketService.getChatHistory().length === 0) {
        webSocketService.loadChatHistory(resume.messages);
        conversationSaverService.restoreConversationState(
          resume.internalInteractionId,
          resume.alreadyPersistedCount,
          resume.previousTimeTotalSeconds,
        );
        logger.log('[Resume] Retomando con misma config tras recarga', {
          id: resume.internalInteractionId,
          messageCount: resume.messages.length,
          mode: resume.mode,
          ageMinutes: Math.round((Date.now() - resume.lastActivityAt) / 60000),
        });
      }
      setUserConfiguration(userConfig);
      setInitialConfigComplete(true);
      previousConfigurationRef.current = null;
      if (!shouldConnect) setShouldConnect(true);
      return;
    }
    
    // Esperar a que los datos de Apolearn estén disponibles antes de setear promptReference
    // El promptReference depende del role del usuario
    let apolearnData = authService.getApolearnUserData();
    let attempts = 0;
    const maxAttempts = 10; // 5 segundos máximo (500ms * 10)
    
    while (!apolearnData || !apolearnData.role) {
      if (attempts >= maxAttempts) {
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      apolearnData = authService.getApolearnUserData();
      attempts++;
    }

    // Determinar promptReference considerando el role
    const role = apolearnData?.role;
    const config = configService.getConfig();
    const isTestingPhase = config.testingPhase ?? true;

    const promptReference = getPromptReferenceWithRole(userConfig.mode, role, isTestingPhase);

    // Actualizar promptReference según el modo y role
    configService.setPromptReference(promptReference);
    
    // Guardar configuración de avatar
    configService.setAvatarType(userConfig.mode);
    
    // Guardar modo y acento en configService para que webSocketService pueda usarlos
    configService.setPracticeModeAndAccent(userConfig.mode, userConfig.acento);
    
    // Establecer configuración en conversationSaverService
    conversationSaverService.setCurrentConfiguration(userConfig.mode, userConfig.rasgo, userConfig.acento);
    
    // Inicializar rastreo del chat (inicialmente cerrado)
    conversationSaverService.setChatVisibility(false);
    
    // Guardar configuración completa
    setUserConfiguration(userConfig);
    setInitialConfigComplete(true);
    
    // El banner de feedback se mostrará automáticamente cuando haya mensajes
    // (se verifica dinámicamente en el render)
    
    // Limpiar el ref de configuración anterior DESPUÉS de guardar la nueva configuración
    // Esto permite que la próxima vez que se presione "Home" y luego "Aceptar", se pueda comparar correctamente
    previousConfigurationRef.current = null;
    
    // Activar conexión WebSocket después de completar configuración
    // Siempre reconectar cuando la configuración cambia o es la primera vez
    logger.log('[WS-DIAG] handleConfigurationComplete → activando shouldConnect', { isSameConfiguration, hasPreviousConfig, currentShouldConnect: shouldConnect });
    if (!isSameConfiguration) {
      if (hasPreviousConfig) {
        // Configuración diferente: ya se desconectó arriba, ahora reconectar
        setTimeout(() => {
          logger.log('[WS-DIAG] setShouldConnect(true) — con config previa');
          setShouldConnect(true);
        }, 200);
      } else {
        // Primera vez: conectar directamente
        logger.log('[WS-DIAG] setShouldConnect(true) — primera vez');
        setShouldConnect(true);
      }
    } else {
      // Misma configuración: solo conectar si no está conectado
      if (!shouldConnect) {
        logger.log('[WS-DIAG] setShouldConnect(true) — misma config');
        setShouldConnect(true);
      }
    }

    // Los mensajes de bienvenida predeterminados han sido eliminados
    // El modo actividades inicia directamente sin mensajes automáticos
    
    // Si hay datos de Apolearn, reactivar el modo académico después de la configuración
    // (apolearnData ya está disponible desde arriba)
    
    // Asegurar que el email en configService coincida con el de Apolearn
    if (apolearnData && apolearnData.user?.email && config.email !== apolearnData.user.email) {
      configService.setUserInfo(
        apolearnData.user.email,
        `${apolearnData.user.firstname || ''} ${apolearnData.user.lastname || ''}`.trim(),
        apolearnData.user.id,
        apolearnData.user.firstname,
        apolearnData.user.lastname
      );
    }
    
    const updatedConfig = configService.getConfig();
    if (apolearnData && updatedConfig.email) {
      academicService.getUserAcademicLevel().then(async (level) => {
        if (level) {
          setAcademicLevel(level);
          setShowAcademicMode(true);
          // Verificar si hay una lección en proceso
          await checkLessonInProgress();
        }
      }).catch(() => {});
    }
  };

  // Modo profesor (aide-prof): toda la interfaz se muestra en francés (el profesor
  // es personal de la AF). En modo estudiante la UI se mantiene en español.
  const isAideProf = userConfiguration?.mode === 'actividades-docente';

  // ── Piezas compartidas por el layout clásico y el «Espace de travail» ──
  // Se arman una sola vez y cada layout las coloca donde le toca; así los dos
  // no pueden divergir en comportamiento.
  const micButton = (
    <MicrophoneButton
      onRecordingComplete={handleRecordingComplete}
      onRecordingStart={() => webSocketService.interruptPlayback()}
      aideProf={isAideProf}
    />
  );
  const stopButton = isGaliaSpeaking ? (
    <button
      className="stop-galia-button"
      onClick={() => {
        webSocketService.interruptPlayback();
        setIsGaliaSpeaking(false);
        setHablando(false);
      }}
      aria-label={isAideProf ? 'Arrêter la réponse de Galia' : 'Detener la respuesta de Galia'}
      title={isAideProf ? 'Arrêter' : 'Detener'}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <rect x="6" y="6" width="12" height="12" rx="2" />
      </svg>
      <span>{isAideProf ? 'Arrêter' : 'Detener'}</span>
    </button>
  ) : null;

  const toolbarButtons = (
    <>
                {/* Altavoz: silenciar/activar la voz (TTS) de Galia */}
                {initialConfigComplete && userConfiguration && (
                  <AudioToggleButton
                    enabled={audioEnabled}
                    onClick={handleToggleAudio}
                    aideProf={isAideProf}
                  />
                )}
                {/* Engranaje: solo aparece tras completar la configuracion inicial */}
                {initialConfigComplete && userConfiguration && (
                  <SettingsButton
                    isOpen={showSettingsPanel}
                    onClick={() => {
                      // Cerrar otros sidebars al abrir Configuracion
                      setIsAcademicProgressVisible(false);
                      setShowTeacherActivityPanel(false);
                      setShowStudentActivityList(false);
                      setShowStudentActivityView(false);
                      handleChatVisibilityChange(false);
                      setShowSettingsPanel((v) => !v);
                    }}
                    aideProf={isAideProf}
                  />
                )}
                {/* Contenidos interactivos Nomade (Geniallys de la AF).
                    En el kiosco no van: Santiago pidió solo la conversación
                    (nada de Nomade ni cursos internos) — 26/08. En el espacio de
                    trabajo el botón abre el visor en su columna, que arranca
                    con la portada de Galia (Santiago, 21/09: Nomade no se
                    propone de entrada, lo elige el estudiante). */}
                {initialConfigComplete && userConfiguration && !isKioskMode() && (
                  <ContentsButton
                    isOpen={showContentViewer && !workspaceLayout}
                    active={workspaceLayout ? showContentViewer : undefined}
                    onClick={() => {
                      // En el espacio de trabajo el botón alterna portada ↔ contenidos.
                      if (workspaceLayout && showContentViewer) {
                        setShowContentViewer(false);
                        return;
                      }
                      // Cerrar otros paneles al abrir el visor de contenidos.
                      // El chat NO se cierra: el visor va acoplado a la derecha
                      // y se puede conversar con Galia mientras se ve el episodio.
                      setIsAcademicProgressVisible(false);
                      setShowTeacherActivityPanel(false);
                      setShowStudentActivityList(false);
                      setShowStudentActivityView(false);
                      setShowSettingsPanel(false);
                      setShowContentViewer(true);
                    }}
                    aideProf={isAideProf}
                  />
                )}

                {/* Nueva conversación: reiniciar el hilo con Galia (pedido D. Soler) */}
                {initialConfigComplete && userConfiguration && (
                  <NewConversationButton
                    onReset={handleResetConversation}
                    aideProf={isAideProf}
                  />
                )}

                {/* Cofre de trofeos (logros por acción) — solo estudiantes, solo build dev */}
                {gamificationUI && initialConfigComplete && userConfiguration && !isAideProf && (
                  <TrophyChestButton
                    isOpen={showTrophyChest}
                    unlockedCount={trophyCount}
                    onClick={() => {
                      // Cerrar otros paneles al abrir el cofre
                      setIsAcademicProgressVisible(false);
                      setShowTeacherActivityPanel(false);
                      setShowStudentActivityList(false);
                      setShowStudentActivityView(false);
                      setShowSettingsPanel(false);
                      handleChatVisibilityChange(false);
                      setShowTrophyChest(true);
                      // Al abrir el cofre, todo queda "visto": el badge se apaga.
                      achievementService
                        .markAllSeen(trophyEmail())
                        .then(() => setTrophyCount(0))
                        .catch(() => setTrophyCount(0));
                    }}
                  />
                )}

                <InfoButton aideProf={isAideProf} onToggle={(visible) => {
                  setIsInformationVisible(visible);
                  if (visible) handleChatVisibilityChange(false);
                }} />

                {/* Ayuda de micrófono (compromiso reunión 18/08: causa raíz de
                    "Galia no funciona" = mic mal seleccionado) — solo estudiantes */}
                {initialConfigComplete && userConfiguration && !isAideProf && (
                  <MicHelpButton onClick={() => setShowMicHelp(true)} />
                )}

    </>
  );

  // Barra «¿Cómo fue tu experiencia?». En el espacio de trabajo pasa a ser una
  // tarjeta flotante pequeña (WorkspaceTheme.css, pedido de Carlos 18/09).
  const feedbackBannerVisible = showFeedbackBanner && initialConfigComplete && messages.length > 0 && messages.some(m => m.role === 'user');

  // Visor de contenidos: modal en el layout clásico, columna en el nuevo.
  const renderContentViewer = (inline: boolean) => {
    // Cours internes (méthode Mon alter ego) es SOLO para el personal de la AF
    // (profesores/administrativos), por dominio del correo. El estudiante solo
    // ve la pestaña Nomade; ni siquiera ve la pestaña Cours internes.
    const isAfStaff = authService.isAfStaffEmail(configService.getConfig().email);
    // Preselección del visor: el estudiante ve por defecto la temporada/libro
    // de SU nivel real en Apolearn (curso del que abrió Galia por LTI). Esto
    // NO cambia el nivel de interacción con Galia (userConfiguration.mcerLevel),
    // que el estudiante sigue eligiendo (pedido Santiago 2026-07-09). El
    // profesor (aide-prof) no tiene "su nivel": usa el configurado. Si no hay
    // dato de Apolearn, cae al nivel configurado (comportamiento anterior).
    const contentLevel = (!isAideProf ? authService.getStudentContentLevel() : null)
      ?? userConfiguration?.mcerLevel;
    return (
      <ContentViewer
        mcerLevel={contentLevel}
        showCoursInternes={isAfStaff}
        isTeacher={isAfStaff}
        onWorkWithContent={isAideProf ? handleWorkWithContent : undefined}
        onClose={() => setShowContentViewer(false)}
        inline={inline}
      />
    );
  };

  // Embebida en un iframe (p. ej. el player de Apolearn): el micrófono se
  // bloquea por Permissions Policy. Ofrecemos reabrir en pestaña nueva
  // (top-level), donde getUserMedia sí funciona. En la interfaz clásica flota
  // arriba; en el espacio de trabajo va como franja propia SOBRE la cabecera,
  // porque flotando tapaba el menú (Santiago, 21/09).
  const embedBanner = isEmbedded() ? (
    <div className="embed-banner" role="alert">
      <span className="embed-banner-text">
        {isAideProf
          ? 'Pour parler à Galia avec la voix, ouvre-la dans un nouvel onglet 🎤'
          : 'Para hablar con Galia por voz, ábrela en una pestaña nueva 🎤'}
      </span>
      <button
        type="button"
        className="embed-banner-btn"
        onClick={() => openStandalone()}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
        </svg>
        {/* Informe UX 11/09/2026, hallazgo 2 (alto): el texto sugería
            pantalla completa en el mismo sitio, pero abre una pestaña
            nueva y deja dos instancias activas. Se nombra lo que hace. */}
        <span>{isAideProf ? 'Ouvrir dans un nouvel onglet' : 'Abrir en una pestaña nueva'}</span>
      </button>
    </div>
  ) : null;

  // Selector Estudiante / Profesor de la cabecera nueva: solo personal de la AF
  // (mismo criterio que la pantalla de configuración). Cambia de modo con la
  // configuración vigente; handleConfigurationComplete ya reinicia audio e
  // historial al cambiar de modo.
  const testingPhaseForRole = configService.getConfig().testingPhase ?? false;
  const isTeacherUser =
    authService.getEffectiveRole(configService.getConfig().email, testingPhaseForRole) === 'teacher';
  const handleSwitchMode = (toAideProf: boolean) => {
    if (!userConfiguration) return;
    const mode = toAideProf ? 'actividades-docente' : 'conversacional';
    void handleConfigurationComplete({
      ...userConfiguration,
      mode,
      promptReference: getPromptReference(mode, testingPhaseForRole),
    });
  };

  return (
    <LayoutManager>
      <div className={`app-container ${isAcademicProgressVisible && configService.getConfig().email ? 'academic-sidebar-visible' : ''} ${showTeacherActivityPanel ? 'teacher-activity-sidebar-visible' : ''} ${showSettingsPanel ? 'settings-sidebar-visible' : ''} ${isInformationVisible ? 'information-visible' : ''}`}>
        {/* Aviso de iframe: aquí solo cuando NO está montado el espacio de
            trabajo (que lo recibe como ranura y lo pone sobre su cabecera). */}
        {!(workspaceLayout && initialConfigComplete && userConfiguration) && embedBanner}
        {(() => {return null;
        })()}
        {/* Formulario de email cuando se accede por URL con tache_id */}
        {showActivityEmailForm && (
          <ActivityEmailForm
            onSubmit={handleActivityEmailSubmit}
            error={activityEmailError}
            loading={activityEmailLoading}
          />
        )}

        {/* Menú inicial solo si no está completada la configuración y no se está mostrando el formulario de email */}
        {!initialConfigComplete && !showActivityEmailForm && (
          <>
            <InitialScreen
              initialEmail={effectiveEmailForInitial}
              onConfigurationComplete={handleConfigurationComplete}
            />
          </>
        )}

        {(() => {return null;
        })()}
        {(() => {const shouldRender = initialConfigComplete && userConfiguration;
          const config = configService.getConfig();if (!shouldRender) {return null;
          }return (
            <>
            {/* Paisaje desbloqueable ("la casa de Galia") — capa de fondo, solo estudiantes.
                Solo build dev mientras la AF trabaja el arte final. */}
            {/* Capa de fondo a pantalla completa: solo en la interfaz clásica. En el
                espacio de trabajo el paisaje va dentro de la portada (prop scene). */}
            {gamificationUI && !isAideProf && !workspaceLayout && (
              <GaliaScene
                email={trophyEmail()}
                mcerLevel={userConfiguration.mcerLevel}
                chatVisible={isChatVisible}
              />
            )}

            {/* Trofeos por acción (saludar 3 veces → trofeo, etc.) — solo estudiantes */}
            {gamificationUI && !isAideProf && (
              <AchievementToast
                queue={achievementQueue}
                onDismiss={(id) =>
                  setAchievementQueue((q) => q.filter((a) => a.id !== id))
                }
              />
            )}

            {/* Cofre de trofeos: galería de logros del estudiante */}
            {gamificationUI && !isAideProf && showTrophyChest && (
              <TrophyChestPanel
                email={trophyEmail()}
                level={userConfiguration.mcerLevel}
                onClose={() => setShowTrophyChest(false)}
              />
            )}

            {/* Panel de ayuda del micrófono */}
            {showMicHelp && (
              <MicHelpPanel onClose={() => setShowMicHelp(false)} />
            )}

            {!workspaceLayout && <LottieAvatar
              className={`main-avatar ${isChatVisible ? 'chat-visible' : ''}`}
              mode={userConfiguration.mode}
              avatarId="avatar1"
            />}

            {/* Chip de configuracion siempre visible — clic abre el sidebar.
                En el espacio de trabajo el nivel va en la cabecera. */}
            {!workspaceLayout && <ConfigChip
              configuration={userConfiguration}
              onClick={() => {
                setIsAcademicProgressVisible(false);
                setShowTeacherActivityPanel(false);
                setShowStudentActivityList(false);
                setShowStudentActivityView(false);
                handleChatVisibilityChange(false);
                setShowSettingsPanel(true);
              }}
            />}

            {/* Tracker de objetivos compacto - mostrar cuando hay una lección activa */}
            {isLessonInProgress && currentLessonObjectives.length > 0 && (
              <div className="objectives-tracker-container">
                <ObjectivesCompactTracker
                  objectives={currentLessonObjectives}
                  progress={currentLessonProgress}
                  onInfoClick={() => setShowObjectivesPanel(true)}
                />
              </div>
            )}
            
            {/* Iconos en las esquinas */}
            <img 
              src="/assets/ui/otros/galia.png" 
              alt="Galia" 
              className="corner-icon corner-icon-top-left"
            />
            <img 
              src="/assets/ui/otros/logo_af.png" 
              alt="Alianza Francesa" 
              className="corner-icon corner-icon-top-right"
            />

            {/* Modo Académico - Mantener montado para preservar estado */}
            {/* Panel siempre disponible cuando se hace clic en el botón */}
            {/* Academic mode — full panel when email exists */}
            {isAcademicProgressVisible && config.email && (
              <div className="academic-mode-container visible">
                <AcademicMode
                  userEmail={config.email}
                  showProgressMap={true}
                  onClose={() => {
                    setIsAcademicProgressVisible(false);
                    checkLessonInProgress().catch(console.error);
                  }}
                  onActivityStart={() => setIsLessonInProgress(true)}
                  onActivityEnd={() => setIsLessonInProgress(false)}
                />
              </div>
            )}

            {/* Academic mode — info card when no email (rendered outside container, centered) */}
            {isAcademicProgressVisible && !config.email && (
              <div className="academic-mode-no-data" onClick={(e) => { if (e.target === e.currentTarget) setIsAcademicProgressVisible(false); }}>
                <div className="academic-no-data-card">
                  <button className="academic-no-data-close" onClick={() => setIsAcademicProgressVisible(false)} aria-label="Cerrar">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                  <div className="academic-no-data-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d4511e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 10l-10-5L2 10l10 5 10-5z"/><path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5"/><path d="M22 10v6"/>
                    </svg>
                  </div>
                  <h3 className="academic-no-data-title">Ruta Académica</h3>
                  <p className="academic-no-data-desc">
                    Lecciones estructuradas con objetivos, evaluaciones y seguimiento de tu progreso en francés.
                  </p>
                  <div className="academic-no-data-features">
                    <div className="academic-no-data-feature">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                      <span>Lecciones por nivel (A1 → C2)</span>
                    </div>
                    <div className="academic-no-data-feature">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                      <span>Objetivos y evaluación con rúbrica</span>
                    </div>
                    <div className="academic-no-data-feature">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                      <span>Tareas personalizadas con IA</span>
                    </div>
                  </div>
                  <div className="academic-no-data-how">
                    <h4>¿Cómo activarla?</h4>
                    <p>Ingresa a Galia desde la plataforma <strong>Apolearn</strong> de tu institución. Tu profesor te compartirá el acceso desde el aula virtual.</p>
                  </div>
                  <button className="academic-no-data-cta" onClick={() => setIsAcademicProgressVisible(false)}>
                    Entendido
                  </button>
                </div>
              </div>
            )}
            </>
          );
        })()}
        
        {!workspaceLayout && (
        <div className={`ui-controls ${isFeedbackBannerExpanded ? 'hidden' : ''}`}>
          <div className="ui-controls-left">
            {micButton}
            {stopButton}
            <Chat
              messages={messages}
              onSendMessage={sendTextMessage}
              onAttachDocument={handleAttachDocument}
              inline={true}
              isVisibleControlled={isChatVisible}
              onVisibilityChange={handleChatVisibilityChange}
              mode={userConfiguration?.mode}
              enableCopyPaste={true} // Habilitado para todos los modos, especialmente útil en modo estudio
              enableAudioReplay={true} // Habilitado para todos los modos
              recentConversations={isKioskMode() ? undefined : chatTabs}
              activeConversationId={activeConversationId}
              onSelectConversation={handleSelectConversation}
              onNewConversation={handleResetConversation}
            />
          </div>
          <div className="ui-controls-right">
            {/* Ocultar botones de la derecha cuando está abierto el panel de ruta académica o el de actividades (solo quedan mic y chat) */}
            {!(isAcademicProgressVisible && configService.getConfig().email) && !showTeacherActivityPanel && !showStudentActivityList && !showStudentActivityView && (
              <>
                {toolbarButtons}
              </>
            )}
          </div>
        </div>
        )}

        {/* Rediseño opción 1a «Espace de travail» (solo dev): contenido y chat
            en dos columnas fijas. Recibe como ranuras los MISMOS elementos que
            la barra clásica — la lógica no se duplica. */}
        {workspaceLayout && initialConfigComplete && userConfiguration && (
          <WorkspaceLayout
            level={userConfiguration.mcerLevel}
            aideProf={isAideProf}
            isTeacher={isTeacherUser}
            onSwitchMode={handleSwitchMode}
            banner={embedBanner}
            headerActions={toolbarButtons}
            // El visor solo se monta cuando se pide: al cerrarlo se desmonta
            // el iframe (igual que el modal clásico) y no queda audio sonando.
            content={showContentViewer ? renderContentViewer(true) : null}
            contentOpen={showContentViewer}
            onOpenContent={() => setShowContentViewer(true)}
            onCloseContent={() => setShowContentViewer(false)}
            // El paisaje provisional vuelve en la portada (solo estudiantes, solo dev).
            scene={gamificationUI && !isAideProf
              ? <GaliaScene email={trophyEmail()} mcerLevel={userConfiguration.mcerLevel} />
              : null}
            mode={userConfiguration.mode}
            firstName={configService.getConfig().userFirstname}
            speakingControl={stopButton}
            chat={
              <Chat
                messages={messages}
                onSendMessage={sendTextMessage}
                onAttachDocument={handleAttachDocument}
                embedded
                inputAccessory={<div className="ws-mic-slot">{micButton}</div>}
                placeholder={isAideProf ? 'Écris à Galia…' : 'Escribe tu respuesta…'}
                mode={userConfiguration?.mode}
                enableCopyPaste={true}
                enableAudioReplay={true}
                recentConversations={chatTabs}
                activeConversationId={activeConversationId}
                onSelectConversation={handleSelectConversation}
                onNewConversation={handleResetConversation}
              />
            }
          />
        )}

        {isConnecting && shouldConnect && (
          <div className="connection-status">
            <div className="connection-spinner"></div>
            <p>{isAideProf ? 'Connexion au serveur…' : 'Conectando con el servidor...'}</p>
          </div>
        )}

        {/* #3 Indicador de transcripción: feedback durante los ~2-4s que tarda
            el STT en convertir el audio a texto. Evita la ambigüedad de "¿me
            escuchó o no?" mientras el usuario espera. */}
        {isTranscribing && (
          <div className="connection-status transcribing-status" aria-live="polite">
            <div className="connection-spinner"></div>
            <p>{isAideProf ? 'Transcription de ton audio…' : 'Transcribiendo tu audio...'}</p>
          </div>
        )}

        {/* #1 Aviso de "no te entendí": antes el fallo del STT era silencioso. */}
        {showNotRecognized && (
          <div className="not-recognized-toast" role="status" aria-live="assertive">
            <p>{isAideProf ? "Je n'ai pas bien compris 🎤 Peux-tu répéter ?" : 'No te entendí bien 🎤 ¿Puedes repetirlo?'}</p>
          </div>
        )}
      </div>

      {/* Banner de feedback - solo mostrar si hay conversación guardable */}
      {feedbackBannerVisible && (
        <FeedbackBanner
          aideProf={isAideProf}
          isVisible={true}
          onSubmit={async (rating, feedback) => {
            await feedbackService.sendFeedback(rating, feedback);
            // Asegurar que los botones reaparezcan
            setIsFeedbackBannerExpanded(false);
            setShowFeedbackBanner(false);
          }}
          onExpandChange={(isExpanded) => {
            setIsFeedbackBannerExpanded(isExpanded);
            // Cerrar el chat cuando el banner se expande
            if (isExpanded && isChatVisible) {
              handleChatVisibilityChange(false);
            }
          }}
        />
      )}

      {/* Componentes de actividades del profesor */}
      {showTeacherActivityPanel && (
        <div className={`teacher-activity-container ${showTeacherActivityPanel ? 'visible' : 'hidden'}`}>
          <TeacherActivityPanel
            onClose={() => setShowTeacherActivityPanel(false)}
            onNewActivity={() => {
              setShowTeacherActivityPanel(false);
              setEditingActivity(null);
              setShowTeacherActivityForm(true);
            }}
            onEditActivity={(activity) => {
              setShowTeacherActivityPanel(false);
              setEditingActivity(activity);
              setShowTeacherActivityForm(true);
            }}
          />
        </div>
      )}

      {showTeacherActivityForm && (
        <TeacherActivityForm
          activity={editingActivity}
          onSave={(activity) => {
            setShowTeacherActivityForm(false);
            setEditingActivity(null);
            setCurrentActivity(activity);
            setShowTeacherActivityPreview(true);
          }}
          onCancel={() => {
            setShowTeacherActivityForm(false);
            setEditingActivity(null);
            setShowTeacherActivityPanel(true);
          }}
        />
      )}

      {showTeacherActivityPreview && currentActivity && (
        <TeacherActivityPreview
          activity={currentActivity}
          onAdjust={() => {
            setShowTeacherActivityPreview(false);
            setEditingActivity(currentActivity);
            setShowTeacherActivityForm(true);
          }}
          onDeployed={(deployedActivity) => {
            setShowTeacherActivityPreview(false);
            setCurrentActivity(null);
            // Mostrar URL en el chat
            if (deployedActivity.tache_id) {
              const url = teacherActivityService.generateActivityUrl(deployedActivity.tache_id);
              const urlMessage = `✅ Actividad desplegada exitosamente!\n\nURL para compartir:\n${url}\n\nCopia esta URL y compártela con tus estudiantes.`;
              webSocketService.addSystemMessage(urlMessage);
            }
            setShowTeacherActivityPanel(true);
          }}
          onCancel={() => {
            setShowTeacherActivityPreview(false);
            setCurrentActivity(null);
            setShowTeacherActivityPanel(true);
          }}
        />
      )}

      {/* Componentes de actividades del estudiante */}
      {showStudentActivityList && (
        <div className={`student-activity-container ${showStudentActivityList ? 'visible' : 'hidden'}`}>
          <StudentActivityList
            onSelectActivity={(activity) => {
              setShowStudentActivityList(false);
              setCurrentActivity(activity);
              setShowStudentActivityView(true);
            }}
            onClose={() => setShowStudentActivityList(false)}
          />
        </div>
      )}

      {showStudentActivityView && currentActivity && (
        <div className={`student-activity-view-container ${showStudentActivityView ? 'visible' : 'hidden'}`}>
          <StudentActivityView
            activity={currentActivity}
            onStartActivity={() => {
              // NO cerrar la vista de actividad ni cambiar al panel de progreso
              // Solo iniciar la actividad (ya se hace en startTeacherActivity)
              // El modo académico se activa internamente pero no mostramos el panel de progreso
              // La vista de actividad se mantiene visible
            }}
            onBack={() => {
              // Volver a la lista de actividades
              setShowStudentActivityView(false);
              setShowStudentActivityList(true);
            }}
            onClose={() => {
              // Solo ocultar la vista, pero mantener la actividad cargada
              // para que el botón pueda volver a abrirla
              setShowStudentActivityView(false);
            }}
          />
        </div>
      )}

      {/* Sidebar de configuracion persistente — reusa InitialScreen */}
      {/* Bienvenida de primera conexión — va antes que los demás paneles para
          que sea lo primero que se ve (informe UX, hallazgos 1/4/7). */}
      {showWelcome && initialConfigComplete && (
        <WelcomeGuide
          aideProf={isAideProf}
          showTrophies={gamificationUI && !isAideProf}
          onClose={() => {
            setShowWelcome(false);
            void achievementService.markWelcomeSeen(trophyEmail());
          }}
        />
      )}

      {showSettingsPanel && initialConfigComplete && (
        <InitialScreen
          variant="sidebar"
          initialEmail={effectiveEmailForInitial}
          currentMode={userConfiguration?.mode}
          audioEnabled={audioEnabled}
          onToggleAudio={handleToggleAudio}
          onClose={() => setShowSettingsPanel(false)}
          onConfigurationComplete={async (cfg) => {
            await handleConfigurationComplete(cfg);
            setShowSettingsPanel(false);
          }}
        />
      )}

      {/* Visor de contenidos interactivos Nomade (Geniallys de la AF) */}
      {showContentViewer && !workspaceLayout && renderContentViewer(false)}

      {/* Panel de detalles de objetivos */}
      {showObjectivesPanel && currentLessonObjectives.length > 0 && (
        <ObjectivesDetailPanel
          objectives={currentLessonObjectives}
          progress={currentLessonProgress}
          onClose={() => setShowObjectivesPanel(false)}
          isOpen={showObjectivesPanel}
        />
      )}
    </LayoutManager>
  );
}

export default App;
